import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, CheckCircle2, Copy, HelpCircle, Loader2, Radio, XCircle, Clock } from 'lucide-react';
import { watchAudit } from '@/lib/firestore';
import { fmtDate } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button, buttonClassName } from '@/components/ui/button';
import type { AuditDoc } from '@/types/models';

/** Étape du suivi d'appairage (docs/PAIRING.md §4). */
export type PairingStep = 'waiting' | 'expired' | 'receiving' | 'processing' | 'ready' | 'failed';

/** Normalise `pairingExpiresAt` (epoch ms ou ISO 8601) en Date. */
export function pairingExpiryDate(value: number | string): Date {
  return new Date(value);
}

/**
 * Déduit l'étape courante du document audit observé en temps réel.
 * `expired` ne s'applique qu'avant tout rachat du code : une fois l'outil
 * appairé, l'expiration du code n'a plus d'effet sur l'upload en cours.
 */
export function pairingStep(audit: AuditDoc | null, expiresAt: Date, now: Date = new Date()): PairingStep {
  if (audit?.status === 'ready') return 'ready';
  if (audit?.status === 'failed') return 'failed';
  if (audit?.status === 'processing') return 'processing';
  if (audit?.pairing?.redeemedAt) return 'receiving';
  return now.getTime() > expiresAt.getTime() ? 'expired' : 'waiting';
}

type DisplayedStep = Exclude<PairingStep, 'expired' | 'failed'>;
const STEP_ORDER: DisplayedStep[] = ['waiting', 'receiving', 'processing', 'ready'];
const STEP_KEY = {
  waiting:    'pairing.step.waiting',
  receiving:  'pairing.step.receiving',
  processing: 'pairing.step.processing',
  ready:      'pairing.step.ready',
} as const;

interface PairingStatusProps {
  auditId: string;
  code: string;
  expiresAt: Date;
  /** Réinitialise la carte (nouvel audit). */
  onReset: () => void;
  /** Horloge injectable (tests). */
  now?: () => Date;
}

/**
 * Carte 2 « Suivi » : code d'appairage affiché une seule fois (grand,
 * copiable), lien « Comment ça marche » et progression temps réel de
 * l'audit via `onSnapshot(audits/{id})`.
 */
export function PairingStatus({ auditId, code, expiresAt, onReset, now = () => new Date() }: PairingStatusProps) {
  const { t, locale } = useT();
  const [audit, setAudit] = useState<AuditDoc | null>(null);
  const [copied, setCopied] = useState(false);
  const [, tick] = useState(0);

  useEffect(() => watchAudit(auditId, setAudit), [auditId]);

  // Re-render à l'échéance du code pour basculer en « expiré » sans événement Firestore.
  useEffect(() => {
    const delay = expiresAt.getTime() - now().getTime();
    if (delay <= 0) return;
    const id = window.setTimeout(() => tick(n => n + 1), Math.min(delay + 50, 2 ** 31 - 1));
    return () => window.clearTimeout(id);
  }, [expiresAt, now]);

  const step = pairingStep(audit, expiresAt, now());
  const activeIdx = step === 'failed' ? 2 : step === 'expired' ? 0 : STEP_ORDER.indexOf(step);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t('pairing.code.copied'));
    } catch {
      toast.error(t('pairing.code.copy_failed'));
    }
  };

  return (
    <div className="mt-3 space-y-4" data-testid="pairing-status">
      <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-4 text-center dark:border-brand-900/40 dark:bg-brand-950/20">
        <div className="text-xs font-medium uppercase tracking-wide text-brand-700 dark:text-brand-300">{t('pairing.code.label')}</div>
        <code
          className="mt-2 block select-all break-all font-mono text-2xl font-semibold tracking-widest text-ink-900 dark:text-white sm:text-3xl"
          aria-label={t('pairing.code.label')}
        >
          {code}
        </code>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <Button variant="secondary" size="sm" onClick={copy} aria-label={t('pairing.code.copy')}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? t('pairing.code.copied_short') : t('pairing.code.copy')}
          </Button>
          <Link to="/help#toolkit-audit" className={buttonClassName('ghost', 'text-xs')}>
            <HelpCircle className="h-3.5 w-3.5" /> {t('pairing.how_it_works')}
          </Link>
        </div>
        <p className="mt-2 text-[0.72rem] text-ink-500">
          <Clock className="mr-0.5 inline h-3 w-3" /> {t('pairing.code.expires', { when: fmtDate(expiresAt, locale) })}
        </p>
      </div>

      <p className="text-xs text-ink-600 dark:text-ink-300">{t('pairing.instructions', { code })}</p>

      <ol className="space-y-2" aria-label={t('pairing.progress')}>
        {STEP_ORDER.map((s, i) => {
          const isFailedSlot = step === 'failed' && i === 3;
          const done = i < activeIdx || step === 'ready';
          const active = i === activeIdx && step !== 'ready';
          const icon = isFailedSlot ? <XCircle className="h-4 w-4 text-rose-500" />
            : done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            : active ? (step === 'expired' ? <Clock className="h-4 w-4 text-amber-500" /> : <Loader2 className="h-4 w-4 animate-spin text-brand-500" />)
            : <Radio className="h-4 w-4 text-slate-300 dark:text-ink-600" />;
          const label = isFailedSlot ? t('pairing.step.failed') : s === 'waiting' && step === 'expired' ? t('pairing.step.expired') : t(STEP_KEY[s]);
          return (
            <li
              key={s}
              data-testid={`pairing-step-${s}`}
              data-state={isFailedSlot ? 'failed' : done ? 'done' : active ? 'active' : 'todo'}
              className={`flex items-center gap-2 text-sm ${active || isFailedSlot ? 'font-medium text-ink-900 dark:text-white' : done ? 'text-ink-600 dark:text-ink-300' : 'text-ink-400'}`}
            >
              {icon}
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      {step === 'expired' && (
        <p className="text-xs text-amber-700 dark:text-amber-400" role="status">{t('pairing.expired.desc')}</p>
      )}
      {step === 'failed' && (
        <p className="text-xs text-rose-700 dark:text-rose-400" role="alert">{audit?.error?.message ?? t('audit.failed.desc')}</p>
      )}
      {step === 'ready' && (
        <Link to={`/audits/${auditId}`} className={buttonClassName('primary', 'w-full')}>{t('upload.success.see')}</Link>
      )}
      {(step === 'ready' || step === 'failed' || step === 'expired') && (
        <Button variant="secondary" className="w-full" onClick={onReset}>{t('pairing.new_audit')}</Button>
      )}
    </div>
  );
}

/** Badge d'en-tête : audit reçu automatiquement via l'outil appairé. */
export function PairingBadge({ audit }: { audit: Pick<AuditDoc, 'pairing'> }) {
  const { t } = useT();
  if (audit.pairing?.uploadedVia !== 'toolkit') return null;
  return (
    <Badge variant="info" title={t('audit.badge.via_toolkit.title')}>
      <Radio className="mr-1 inline h-3 w-3" />{t('audit.badge.via_toolkit')}
    </Badge>
  );
}
